import React, { useState, useRef, useEffect } from 'react'
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock.svg'
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg'
import CustomDateRangePopup from './CustomDateRangePopup'
import {
    DATE_RANGE_OPTIONS,
    DateRangeType,
    getDateRangeBounds,
    getDateRangeLabel
} from './dateRangeOptions'

// Reusable date range dropdown used across the dashboard tabs.
// Controlled via `value` ({ type, fromDate, toDate }) and `onChange`.
const DateRangeDropdown = ({ value, onChange }) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const [showCustom, setShowCustom] = useState(false)
    const containerRef = useRef(null)
    const triggerRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = (type) => {
        if (type === DateRangeType.CUSTOM) {
            setMenuOpen(false)
            setShowCustom(true)
            return
        }
        onChange({ type, ...getDateRangeBounds(type) })
        setMenuOpen(false)
    }

    const handleApplyCustom = (fromDate, toDate) => {
        onChange({ type: DateRangeType.CUSTOM, fromDate, toDate })
        setShowCustom(false)
    }

    return (
        <div className='relative' ref={containerRef}>
            <button
                type='button'
                ref={triggerRef}
                onClick={() => {
                    setShowCustom(false)
                    setMenuOpen((prev) => !prev)
                }}
                className='flex items-center gap-2 border border-[#e9e9ea] bg-white px-3 py-2 regular-14 text-[#21242b]'
            >
                <ClockIcon className='w-4 h-4' />
                <span>{getDateRangeLabel(value)}</span>
                <ArrowDownIcon className='w-3.5 h-3.5' />
            </button>

            {menuOpen && (
                <ul className='absolute right-0 mt-2 w-44 bg-white border border-[#e9e9ea] shadow-lg z-50 py-1'>
                    {DATE_RANGE_OPTIONS.map((option) => (
                        <li key={option.type}>
                            <button
                                type='button'
                                onClick={() => handleSelect(option.type)}
                                className={`w-full text-left px-4 py-2 regular-14 hover:bg-[#f8f8f8] ${
                                    value?.type === option.type
                                        ? 'text-[#266ef1] weight-500'
                                        : 'text-[#4d5055]'
                                }`}
                            >
                                {option.label}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <CustomDateRangePopup
                isOpen={showCustom}
                onClose={() => setShowCustom(false)}
                onApply={handleApplyCustom}
                initialFromDate={
                    value?.type === DateRangeType.CUSTOM
                        ? value.fromDate
                        : undefined
                }
                initialToDate={
                    value?.type === DateRangeType.CUSTOM
                        ? value.toDate
                        : undefined
                }
                triggerRef={triggerRef}
            />
        </div>
    )
}

export default DateRangeDropdown
